/**
 * Design Library — pattern validator.
 *
 * Data-driven: iterates the schema's field lists rather than hard-coding
 * per-field logic. Enforces integrity where it matters — genome references,
 * capability vocabulary, pattern relationships — and purity (a pattern may
 * never carry pixels, geometry, or editor concepts). Deterministic and
 * non-throwing.
 */

import {
  ALLOWED_PATTERN_KEYS, FAMILIES, CAPABILITIES, IDENTITY_FIELDS, PROSE_FIELDS,
  FREEFORM_LIST_FIELDS, GENOME_REF_FIELD, CAPABILITY_FIELD, PATTERN_REF_FIELDS,
} from './librarySchema';

const isStr = (v) => typeof v === 'string' && v.length > 0;
const isArrOfStr = (v) => Array.isArray(v) && v.every(isStr);
const isPlainObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Keys/values that would mean the library had leaked into rendering territory. */
const FORBIDDEN = /\b(px|x|y|width|height|fabric|canvas|svg|react|geometry|coordinates?)\b/i;

/**
 * @param {*} pattern
 * @param {Object} [ctx]
 * @param {Set<string>} [ctx.genomeIds]   valid genome ids (cross-ref).
 * @param {Set<string>} [ctx.patternIds]  valid pattern ids (relationships).
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validatePattern = (pattern, ctx = {}) => {
  const errors = [];
  const label = (pattern && pattern.id) || 'pattern';
  if (!isPlainObj(pattern)) return { valid: false, errors: ['pattern must be an object'] };

  Object.keys(pattern).forEach((key) => {
    if (!ALLOWED_PATTERN_KEYS.includes(key)) errors.push(`${label}.${key}: unknown field`);
  });

  IDENTITY_FIELDS.forEach((key) => { if (!isStr(pattern[key])) errors.push(`${label}.${key}: required non-empty string`); });
  if (!FAMILIES.includes(pattern.family)) errors.push(`${label}.family: "${pattern.family}" is not a known family`);
  if (pattern.version !== undefined && (typeof pattern.version !== 'number' || pattern.version < 1)) {
    errors.push(`${label}.version: must be a number >= 1`);
  }

  PROSE_FIELDS.forEach((key) => { if (!isStr(pattern[key])) errors.push(`${label}.${key}: required non-empty string`); });

  FREEFORM_LIST_FIELDS.forEach((key) => {
    if (!isArrOfStr(pattern[key]) || pattern[key].length === 0) errors.push(`${label}.${key}: required non-empty array of strings`);
  });

  // Genome cross-references.
  if (!isArrOfStr(pattern[GENOME_REF_FIELD]) || pattern[GENOME_REF_FIELD].length === 0) {
    errors.push(`${label}.${GENOME_REF_FIELD}: required non-empty array of genome ids`);
  } else if (ctx.genomeIds) {
    pattern[GENOME_REF_FIELD].forEach((id) => {
      if (!ctx.genomeIds.has(id)) errors.push(`${label}.${GENOME_REF_FIELD}: "${id}" is not a known genome`);
    });
  }

  // Capability vocabulary.
  if (!isArrOfStr(pattern[CAPABILITY_FIELD]) || pattern[CAPABILITY_FIELD].length === 0) {
    errors.push(`${label}.${CAPABILITY_FIELD}: required non-empty array`);
  } else {
    pattern[CAPABILITY_FIELD].forEach((c) => {
      if (!CAPABILITIES.includes(c)) errors.push(`${label}.${CAPABILITY_FIELD}: "${c}" is not a known capability`);
    });
  }

  // Pattern relationships + variant parent.
  PATTERN_REF_FIELDS.forEach((key) => {
    const value = pattern[key];
    if (value === undefined) return;
    if (!Array.isArray(value) || !value.every(isStr)) { errors.push(`${label}.${key}: must be an array of pattern ids`); return; }
    if (ctx.patternIds) {
      value.forEach((id) => {
        if (!ctx.patternIds.has(id)) errors.push(`${label}.${key}: "${id}" is not a known pattern`);
        if (id === pattern.id) errors.push(`${label}.${key}: a pattern cannot reference itself`);
      });
    }
  });
  if (pattern.variantOf !== null && pattern.variantOf !== undefined) {
    if (!isStr(pattern.variantOf)) errors.push(`${label}.variantOf: must be a pattern id`);
    else if (ctx.patternIds && !ctx.patternIds.has(pattern.variantOf)) errors.push(`${label}.variantOf: "${pattern.variantOf}" is not a known pattern`);
  }

  // Expansion rules — semantic seeds only (strings / string lists, never numbers).
  if (!isPlainObj(pattern.expansionRules) || Object.keys(pattern.expansionRules).length === 0) {
    errors.push(`${label}.expansionRules: required non-empty object`);
  } else {
    Object.entries(pattern.expansionRules).forEach(([k, v]) => {
      if (!isStr(v) && !isArrOfStr(v)) errors.push(`${label}.expansionRules.${k}: must be a string or array of strings (no numbers or geometry)`);
    });
  }

  // Purity: no rendering/geometry vocabulary anywhere in the pattern's keys.
  Object.keys(pattern).forEach((key) => {
    if (FORBIDDEN.test(key)) errors.push(`${label}.${key}: forbidden rendering/geometry concept`);
  });

  return { valid: errors.length === 0, errors };
};
